import { type FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import oauthApi from '@/api/oauth.api';
import Button from '@/components/ui/Button';

/**
 * OAuth 2.0 consent page. Claude.ai / ChatGPT send users here after they
 * configured their connector. We inspect the URL params, verify login state,
 * then POST to /api/v1/oauth/consent to get the redirect URL with the code.
 */
export const OAuthAuthorizePage: FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const raw = useMemo(
    () => ({
      response_type: params.get('response_type'),
      client_id: params.get('client_id') ?? '',
      redirect_uri: params.get('redirect_uri') ?? '',
      code_challenge: params.get('code_challenge') ?? undefined,
      code_challenge_method: (params.get('code_challenge_method') ?? undefined) as
        | 'S256'
        | 'plain'
        | undefined,
      state: params.get('state') ?? undefined,
      scope: params.get('scope') ?? undefined,
    }),
    [params],
  );

  // If user isn't logged in, bounce through /login with a return URL.
  useEffect(() => {
    if (!isAuthenticated) {
      const returnUrl = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?return=${encodeURIComponent(returnUrl)}`, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const problems: string[] = [];
  if (raw.response_type !== 'code') problems.push('response_type must be "code"');
  if (!raw.client_id) problems.push('client_id is required');
  if (!raw.redirect_uri) problems.push('redirect_uri is required');

  const handleApprove = async () => {
    setBusy(true);
    setError(null);
    try {
      const { redirectUrl } = await oauthApi.consent({
        client_id: raw.client_id,
        redirect_uri: raw.redirect_uri,
        code_challenge: raw.code_challenge,
        code_challenge_method: raw.code_challenge_method,
        state: raw.state,
        scope: raw.scope,
      });
      window.location.replace(redirectUrl);
    } catch (err) {
      const axiosMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const msg = axiosMsg || (err instanceof Error ? err.message : String(err));
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setBusy(false);
    }
  };

  const handleDeny = () => {
    if (!raw.redirect_uri) {
      navigate('/dashboard', { replace: true });
      return;
    }
    const url = new URL(raw.redirect_uri);
    url.searchParams.set('error', 'access_denied');
    if (raw.state) url.searchParams.set('state', raw.state);
    window.location.replace(url.toString());
  };

  if (!isAuthenticated) return null;

  return (
    <div
      style={{
        minHeight: 'calc(100vh - var(--header-height))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-lg)',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-xl)',
        }}
      >
        <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, marginBottom: 'var(--spacing-sm)' }}>
          Autoriser l'accès
        </h1>
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            marginBottom: 'var(--spacing-lg)',
            lineHeight: 1.6,
          }}
        >
          Une application externe souhaite accéder à StratMap en votre nom (<strong>{user?.firstName} {user?.lastName}</strong>).
          Elle pourra lire et modifier vos workspaces, canvases, noeuds et liaisons.
        </p>

        <div
          style={{
            padding: 'var(--spacing-md)',
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-lg)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <Row label="Application" value={raw.client_id} mono />
          <Row label="Redirige vers" value={raw.redirect_uri} mono truncate />
          <Row label="Scope" value={raw.scope ?? 'mcp'} />
        </div>

        {problems.length > 0 && (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              backgroundColor: 'rgba(239,68,68,0.12)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: 'var(--spacing-md)',
            }}
          >
            Requête invalide : {problems.join(' · ')}
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              backgroundColor: 'rgba(239,68,68,0.12)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: 'var(--spacing-md)',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
          <Button variant="secondary" onClick={handleDeny} disabled={busy} style={{ flex: 1 }}>
            Refuser
          </Button>
          <Button
            onClick={handleApprove}
            isLoading={busy}
            disabled={problems.length > 0}
            style={{ flex: 2 }}
          >
            Autoriser
          </Button>
        </div>
      </div>
    </div>
  );
};

const Row: FC<{ label: string; value: string; mono?: boolean; truncate?: boolean }> = ({
  label,
  value,
  mono,
  truncate,
}) => (
  <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
    <div style={{ color: 'var(--color-text-muted)', width: 120, flexShrink: 0 }}>{label}</div>
    <div
      style={{
        flex: 1,
        fontFamily: mono ? 'monospace' : 'inherit',
        fontSize: mono ? 12 : 'inherit',
        overflow: truncate ? 'hidden' : undefined,
        textOverflow: truncate ? 'ellipsis' : undefined,
        whiteSpace: truncate ? 'nowrap' : undefined,
        wordBreak: truncate ? undefined : 'break-all',
      }}
    >
      {value}
    </div>
  </div>
);

export default OAuthAuthorizePage;
